import type { FC } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material'
import { Check, Remove } from '@mui/icons-material'
import { WizardModal } from '@/common/components'
import type { OnboardingFlags } from '../hooks'
import type { StepConfig } from '../types'
import { OnboardingWizard } from './OnboardingWizard'

interface OnboardingBannerProps {
  readonly wizardSteps: readonly StepConfig[]
  readonly isReturningUser: boolean
  readonly onboardingFlags: OnboardingFlags
}

export const OnboardingBanner: FC<OnboardingBannerProps> = ({
  wizardSteps,
  isReturningUser,
  onboardingFlags,
}) => {
  const [isWizardOpen, setIsWizardOpen] = useState(false)

  const handleOpenWizard = useCallback(() => {
    setIsWizardOpen(true)
  }, [])

  const handleCloseWizard = useCallback(() => {
    setIsWizardOpen(false)
  }, [])

  return (
    <Box sx={sectionSx}>
      <Alert severity="warning" sx={alertSx}>
        <AlertTitle>Setup Incomplete</AlertTitle>

        <Box
          component="ul"
          sx={stepListSx}
          aria-label="Onboarding progress"
          role="status"
          aria-live="polite"
        >
          {ONBOARDING_STEPS.map(({ label, flag }) => {
            const isComplete = onboardingFlags[flag]
            return (
              <Box component="li" key={flag} sx={stepItemSx}>
                {isComplete ? (
                  <>
                    <Check sx={completeIconSx} aria-hidden />
                    <Box component="span" sx={visuallyHiddenSx}>
                      Complete
                    </Box>
                  </>
                ) : (
                  <>
                    <Remove sx={incompleteIconSx} aria-hidden />
                    <Box component="span" sx={visuallyHiddenSx}>
                      Incomplete
                    </Box>
                  </>
                )}
                <Typography variant="body2" sx={stepLabelSx}>
                  {label}
                </Typography>
              </Box>
            )
          })}
        </Box>

        <Box sx={buttonContainerSx}>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleOpenWizard}
            aria-label="Open onboarding wizard to complete setup steps"
          >
            Complete Setup
          </Button>
        </Box>
      </Alert>

      <WizardModal open={isWizardOpen} onClose={handleCloseWizard} maxWidth="md">
        {isWizardOpen && (
          <OnboardingWizard
            steps={wizardSteps}
            isReturningUser={isReturningUser}
            onClose={handleCloseWizard}
          />
        )}
      </WizardModal>
    </Box>
  )
}

const ONBOARDING_STEPS = [
  { label: 'Encryption', flag: 'keyRingConfigured' as const },
  { label: 'API Key', flag: 'apiKeyGenerated' as const },
  { label: 'Host', flag: 'hostRegistered' as const },
] satisfies ReadonlyArray<{ label: string; flag: keyof OnboardingFlags }>

const sectionSx: SxProps<Theme> = {
  py: 2,
  borderBottom: 1,
  borderColor: 'divider',
}

const alertSx: SxProps<Theme> = {
  '& .MuiAlert-message': {
    width: '100%',
  },
}

const stepListSx: SxProps<Theme> = {
  listStyle: 'none',
  p: 0,
  m: 0,
  mt: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
}

const stepItemSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

const completeIconSx: SxProps<Theme> = {
  fontSize: 16,
  color: 'success.main',
}

const incompleteIconSx: SxProps<Theme> = {
  fontSize: 16,
  color: 'text.disabled',
}

const stepLabelSx: SxProps<Theme> = {
  color: 'text.primary',
}

const buttonContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  mt: 2,
}

const visuallyHiddenSx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
}
